import { useState, useEffect, useRef, CSSProperties, useMemo } from "react";
import {
  bedroomLookup,
  mlsAreas,
  mlsStatusLoggedinOptions,
  mlsStatusOptions,
  saleMinPriceLookup,
  saleMaxPriceLookup,
  rentMinPriceLookup,
  rentMaxPriceLookup,
  bedroomLookupMax,
  bathroomLookup,
  bathroomLookupMax,
  buildingLookup,
  buildingLookupMax,
  priceSqtMax,
  priceSqt,
  lotSqt,
  lotSqtMax,
  lotAcr,
  lotAcrMax,
  garageLookup,
  garageLookupMax,
  yearBuild,
  storiesLookup,
  styleLookup,
  constructionLookup,
  parkingSpacesOptions,
  financeOptions,
  maintenanceFeeOptions,
  countiesLookup,
  areas,
  propertyTypeOptions,
  propertyStatusSaleOptions,
  propertyStatusRentOptions,
  driveTimeArrivalTimeOptions,
  driveTimeTravelTimeOptions,
} from "utils/filtersData";
import { DEFAULT_FILTERS, FilterAmenities, filterAmenitiesOptions, IFilters, IFiltersProps } from "./Filters.def";
import { HarExtraFilters } from "./HarExtraFilters";
import { useUserContext } from "services/UserContext";
import { MultiSelect } from "react-multi-select-component";
import { Tab, Tabs } from "react-bootstrap";
import { Typeahead } from "components/Typeahead/Typeahead";
import { RemoveFilterButton } from "components/RemoveFilterButton";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { DriveTimeSuggestion, IItem } from "components/Typeahead/Typeahead.def";
import { captureException } from "scripts/logging/error-report";
import "./Filters.scss";
import { AsyncMultiSelect } from "components/AsyncMultiSelect";
import { getLongValue, typedKeys } from "utils/helpers";
import { ILabeledField } from "operations/searches/search-filter-operation-model";
import { trackEvent } from "services/api-client/matomoTracker";

/** Statuses that make the "For" (Sale/Rent) filter meaningless — when one of
 *  these is active we clear the "For" value so the UI doesn't show a stale
 *  selection.  Shared between the initial-mount check and handleSetSelectFilter. */
export const STATUSES_THAT_CLEAR_FOR: readonly string[] = ["closd", "exp", "term", "with"];


/** Filters this modal has no control for: the geo scoping the original search
 *  ran with (radius / map bounds / drawn polygon), the native passthrough bag
 *  and the sort. Reset drops them instead of blanking them — `fromUIToFilters`
 *  reads the geo keys with `!= null`, so an empty string would be forwarded as
 *  a real filter value. */
const PASSTHROUGH_FILTER_KEYS: readonly (keyof IFilters)[] = [
  "latitude",
  "longitude",
  "radius",
  "map_tools_nwlat",
  "map_tools_nwlng",
  "map_tools_selat",
  "map_tools_selng",
  "map_tools_polygon",
  "extra",
  "sort",
];

/** Typeahead-backed filters, held as `{ value, label }`. Reset clears them by
 *  omission, like their pill/`unSelected` handlers do: an empty sentinel object
 *  stays truthy and serializes as a filter. */
const LABELED_FILTER_KEYS: readonly (keyof IFilters)[] = [
  "neighborhood",
  "nid",
  "County",
  "school_district",
  "elemantary_school",
  "middle_school",
  "high_school",
  "school_id",
  "listing_agentid",
];

/** Multi-valued filters — these clear to an empty array, not to "". */
const ARRAY_FILTER_KEYS: readonly (keyof IFilters)[] = [
  "PropertyType",
  "City",
  "ZipCode",
  "Stories",
  "amenities",
  "Category",
  "community",
];

/** Typeaheads that take the typed text as `query`; any other type is a school level. */
const QUERY_TYPEAHEAD_URLS: Record<string, string | undefined> = {
  addresses: "/api/v1/chatbot/address-typeahead",
  drivetime: "/api/v1/chatbot/drivetime-typeahead",
};

/** Display text for a labeled location filter: the resolved name, falling back
 *  to the raw id the way the filter pills do, so the pill and its remove button
 *  never render an empty label. */
function labeledName(field?: ILabeledField<string | number>): string {
  return field?.label || (field?.value != null ? String(field.value) : "");
}


export function Filter(props: IFiltersProps) {
  const { filters, setFilters, closePopup, send } = props;
  // console.log("Filter component props:", filters);

  const { user } = useUserContext();
  const abortControllers = useRef<Record<string, AbortController>>({});
  const propertyTypeScrollRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const [isOpenMoreFilter, setIsOpenMoreFilter] = useState(false);
  const [priceMax, setPriceMax] = useState(filters.For === "Rent" ? rentMaxPriceLookup : saleMaxPriceLookup);
  const [bedroomMax, setBedroomMax] = useState(bedroomLookupMax);
  const [fullbathMax, setFullbathMax] = useState(bathroomLookupMax);
  const [buildingSQFTMin, setBuildingSQFTMin] = useState(buildingLookupMax);
  const [priceSqMax, setPriceSqMax] = useState(priceSqtMax);
  const [lotSqMax, setLotSqMax] = useState(lotSqtMax);
  const [lotacrMax, setLotacrMax] = useState(lotAcrMax);
  const [yearBuildLookup, setYearBuildLookup] = useState(yearBuild);
  const [searchedList, setSearchedList] = useState({
    D: [],
    E: [],
    M: [],
    H: [],
    addresses: [],
    drivetime: [],
  });
  // Track previous value of "For" to detect actual changes and avoid StrictMode double-fire issues
  const prevForRef = useRef(filters.For);

  // Filter status options based on "For" selection (Sale/Rent)
  const filteredStatusOptions = useMemo(() => {
    const baseOptions = user?.user_id && (user?.role_id ?? 0) > 1
      ? mlsStatusLoggedinOptions
      : mlsStatusOptions;

    // If "For Rent" is selected, exclude "Sold/Rented" option
    if (filters.For === "Rent") {
      return baseOptions.filter(option => option.value !== 'closd');
    }

    // If "Sold/Rented" is selected, exclude "For Rent" by disabling 'A' with rent context
    // But we can't actually disable radio button logic here, so we just filter status
    return baseOptions;
  }, [filters.For, user?.user_id, user?.role_id]);

  // Auto-reset Status if incompatible combination selected
  useEffect(() => {
    // If "For Rent" selected and "Sold/Rented" is selected, clear Status
    if (filters.For === "Rent" && filters.Status === "closd") {
      setFilters(prev => ({ ...prev, Status: "" }));
    }
  }, [filters.For, filters.Status, setFilters]);

  // Check on initial mount: if Status has certain values, set For to empty string
  useEffect(() => {
    if (isInitialMount.current && filters.Status && STATUSES_THAT_CLEAR_FOR.includes(filters.Status)) {
      setFilters(prev => ({ ...prev, For: "" }));
    }
    isInitialMount.current = false;
  }, [filters.Status, setFilters]);

  // Reset price values when switching between Sale and Rent
  useEffect(() => {
    const prevFor = prevForRef.current;

    // If "For" hasn't actually changed, do nothing (covers initial mount and redundant renders) OR Don't reset prices when For is cleared (e.g., by status selection like "Sold/Rented")
    if (prevFor === filters.For || !filters.For) {
      return;
    }

    // Update stored previous value for next run
    prevForRef.current = filters.For;

    setFilters(prev => ({
      ...prev,
      MinimumPrice: "",
      MaximumPrice: ""
    }));
    // Also update the priceMax state based on new For value
    setPriceMax(filters.For === "Rent" ? rentMaxPriceLookup : saleMaxPriceLookup);
  }, [filters.For, setFilters]);

  const handleSetFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSetSelectFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    const updatedFilters = { ...filters, [name]: value };

    // County is a labeled field, not a bare value: keep the option's name next
    // to its FIPS code so the pill can render it without a second lookup. The
    // placeholder option ("") clears the filter by omission.
    if (name === "County") {
      updatedFilters.County = value
        ? { value, label: getLongValue(countiesLookup, value) || "" }
        : undefined;
    }

    if (name === "Status" && STATUSES_THAT_CLEAR_FOR.includes(value)) {
      updatedFilters.For = "";
    }

    setFilters(updatedFilters);
  };

  const handleSetSelectFlatMultiFilter = (
    e: Array<{ value: string; label: string }>,
    filterName: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: e.map(({ value }) => value),
    });
  };

  const handleSetTypeAheadFilter = <K extends keyof IFilters>(name: K, value: IFilters[K]) => {
    setFilters({ ...filters, [name]: value });
    setSearchedList({ D: [], E: [], M: [], H: [], addresses: [], drivetime: [] });
  };

  // const handleSetSelectMultiFilter = (
  //   e: Array<{ value: string; label: string }>,
  //   filterName: string
  // ) => {
  //   setFilters({ ...filters, [filterName]: e.map(({ value }) => value) });
  // };

  const handleResetFilters = () => {
    setFilters((prev) => {
      const cleared: Record<string, unknown> = {};
      for (const key of typedKeys(prev)) {
        // Keys cleared by omission: keeping them as "" would either serialize as
        // a filter (see the key lists above) or leave a stale label behind.
        if (PASSTHROUGH_FILTER_KEYS.includes(key) || LABELED_FILTER_KEYS.includes(key)) {
          continue;
        }
        // Scalars stay as "" so their <select>/<input> remain controlled.
        cleared[key] = ARRAY_FILTER_KEYS.includes(key) ? [] : "";
      }

      // Defaults last so they win over the blanked keys: "For" has no cleared
      // state in the UI, so blanking it left the modal showing "For Sale" while
      // Apply sent an unfiltered search.
      return { ...cleared, ...DEFAULT_FILTERS };
    });
  };

  const handleCheckboxSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    const current = filters[name as keyof typeof filters] as string[] | undefined;
    let updatedPropertyTypes = current ? [...current] : [];
    if (checked) {
      updatedPropertyTypes.push(value);
    } else {
      updatedPropertyTypes = updatedPropertyTypes.filter((type) => type !== value);
    }

    // Preserve scroll position
    const scrollLeft = propertyTypeScrollRef.current?.scrollLeft || 0;
    setFilters({ ...filters, [name]: updatedPropertyTypes });

    // Restore scroll position after state update
    requestAnimationFrame(() => {
      if (propertyTypeScrollRef.current) {
        propertyTypeScrollRef.current.scrollLeft = scrollLeft;
      }
    });
  };

  function getLabelByValue(
    array: ReadonlyArray<{ value: string; label: string }>,
    value: string
  ) {
    const item = array.find((item) => item.value === value?.toString());
    return item ? item.label : null;
  }

  const onOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    e.preventDefault();
    closePopup?.();
  };

  const handleSearch = async (type: string, text: string) => {
    try {
      // cancel any in-flight request for this type
      abortControllers.current[type]?.abort();

      // start a fresh request
      const controller = new AbortController();
      abortControllers.current[type] = controller;

      const queryUrl = QUERY_TYPEAHEAD_URLS[type];
      const { data } = await fetchWrapper.get(
        queryUrl ?
          `${queryUrl}?query=${encodeURIComponent(text)}` :
          `/api/v1/chatbot/schoolslist?type=${type}&search_term=${encodeURIComponent(
            text
          )}`,
        controller.signal
      );
      // ignore data from aborted calls
      if (!controller.signal.aborted) {
        setSearchedList((prev) => ({
          ...prev,
          [type]:
            // doing this thing to just show no data found after search not showing no data found for some interval of time when initially type search query
            data?.length > 0
              ? data
              : [{ mytype: "None" }],
        }));
      }
    } catch (e: any) {
      // AbortError is expected when we cancel a request – silently ignore it
      if (e?.name !== "AbortError") {
        captureException(e);
        console.error(e);
        // Show "No data found" instead of leaving the Typeahead on "Loading…"
        setSearchedList((prev) => ({ ...prev, [type]: [{ mytype: "None" }] }));
      }
    }
  };

  // Closing the modal aborts the typeahead searches still in flight. The ref's
  // object is mutated in place, never replaced, so this reference stays current.
  useEffect(() => {
    const controllers = abortControllers.current;
    return () => Object.values(controllers).forEach((controller) => controller.abort());
  }, []);

  useEffect(() => {
    if (filters.MinimumPrice || filters.MinimumPrice == "") {
      const maxLookup = filters.For === "Rent" ? rentMaxPriceLookup : saleMaxPriceLookup;
      setPriceMax(
        filters.MinimumPrice == ""
          ? maxLookup
          : maxLookup.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.MinimumPrice ?? "0")
          )
      );
    }
    if (filters.BedroomMin || filters.BedroomMin == "") {
      setBedroomMax(
        filters.BedroomMin == ""
          ? bedroomLookupMax
          : bedroomLookupMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.BedroomMin ?? "0")
          )
      );
    }
    if (filters.FullbathMin || filters.FullbathMin == "") {
      setFullbathMax(
        filters.FullbathMin == ""
          ? bathroomLookupMax
          : bathroomLookupMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.FullbathMin ?? "0")
          )
      );
    }
    if (filters.buildingSQFTMin || filters.buildingSQFTMin == "") {
      setBuildingSQFTMin(
        filters.buildingSQFTMin == ""
          ? buildingLookupMax
          : buildingLookupMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.buildingSQFTMin ?? "0")
          )
      );
    }

    if (filters.PricesqftMin || filters.PricesqftMin == "") {
      setPriceSqMax(
        filters.PricesqftMin == ""
          ? priceSqtMax
          : priceSqtMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.PricesqftMin ?? "0")
          )
      );
    }
    if (filters.LotsizeMin || filters.LotsizeMin == "") {
      setLotSqMax(
        filters.LotsizeMin == ""
          ? lotSqtMax
          : lotSqtMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.LotsizeMin ?? "0")
          )
      );
    }
    if (filters.LotAcresMin || filters.LotAcresMin == "") {
      setLotacrMax(
        filters.LotAcresMin == ""
          ? lotAcrMax
          : lotAcrMax.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.LotAcresMin ?? "0")
          )
      );
    }
    if (filters.YearbuildsMin || filters.YearbuildsMin == "") {
      setYearBuildLookup(
        filters.YearbuildsMin == ""
          ? yearBuild
          : yearBuild.filter(
            ({ value }) =>
              parseInt(value) >= parseInt(filters.YearbuildsMin ?? "0")
          )
      );
    }
  }, [filters]);

  const onAmenitiesChange = (e: Array<{ value: FilterAmenities; label: string }>) => {
    let selectedOptions = [...e];

    // Check if both "pool" and "no_pool" are selected
    const hasPool = selectedOptions.some((opt) => opt.value === "pool");
    const hasNoPool = selectedOptions.some((opt) => opt.value === "no_pool");

    // Remove conflicting option
    if (hasPool && hasNoPool) {
      // Decide which one to keep based on the last selected
      const lastSelected = selectedOptions[selectedOptions.length - 1];

      if (lastSelected.value === "pool") {
        selectedOptions = selectedOptions.filter(
          (opt) => opt.value !== "no_pool"
        );
      } else if (lastSelected.value === "no_pool") {
        selectedOptions = selectedOptions.filter((opt) => opt.value !== "pool");
      }
    }

    setFilters({
      ...filters,
      amenities: selectedOptions.map(({ value }) => value),
    });
  };

  const amenityValue = Array.isArray(filters?.amenities)
    ? filters?.amenities?.map((value) => ({
      value,
      label: getLabelByValue(filterAmenitiesOptions, value) ?? "",
    }))
    : [];

  const categoryValue = Array.isArray(filters?.Category)
    ? filters?.Category?.map((value) => ({
      value,
      label:
        getLabelByValue(
          filters.For === "Rent"
            ? propertyStatusRentOptions
            : propertyStatusSaleOptions,
          value
        ) ?? "",
    }))
    : [];

  const storyValue = Array.isArray(filters?.Stories)
    ? filters?.Stories?.map((value) => ({
      value,
      label: getLabelByValue(storiesLookup, value) ?? "",
    }))
    : [];

  const getValue = (value?: string | string[]) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((v) => ({ value: v, label: v }));
    return value.split(",").map((v) => ({ value: v.trim(), label: v.trim() })).filter(item => item.value);
  }

  const resetSchoolList = (schoolType: string) => {
    setSearchedList((prev) => ({ ...prev, [schoolType]: [] }));
  };

  function PropertyType(props: { id: string, icon: string, label: string, value: string, checked?: boolean }) {
    const { id, value, icon, label, checked } = props;
    const style: CSSProperties = {
      backgroundImage: `url(${icon})`,
    }

    return (
      <div className="choosable_image m-0" style={{ flexShrink: 0, minWidth: '120px' }}>
        <input
          type="checkbox"
          className="custom-control-input"
          value={value}
          name="PropertyType"
          id={id}
          checked={checked}
          onChange={handleCheckboxSelection}
        />

        <label className="custom-control-label" htmlFor={id} style={style}>
          <span>{label}</span>
        </label>
      </div>
    )
  }
  return (
    <div
      className="property_overlay search_filters d-block"
      onClick={onOverlayClick}
    >
      <div className="property_window">
        <div className="pw_header d-flex mb-1 pl-4 align-items-center position-static">
          <div className="font_size--large font_weight--bold pt-4 mb-4">
            Search Filters
          </div>
          <div className="ml-auto btn_close_popup" onClick={closePopup}>
            <img src="https://content.harstatic.com/media/icons/close_black.svg" />
          </div>
        </div>

        <Tabs
          defaultActiveKey="Location"
          transition={false}
          id="FiltersTab"
          className="nav nav-tabs nav-tabs--simple "
        >
          <Tab
            eventKey="Location"
            title="Location"
            className="mt-3 animate animate__slideInUp"
          >
            <div className="px-4 pb-4 pt-4 mb-4" style={{ backgroundColor: '#eee' }}>
              <label className="mb-1" htmlFor="city_id">
                City
              </label>
              <AsyncMultiSelect
                searchType="city"
                value={getValue(filters?.City)}
                onChange={(items) =>
                  handleSetSelectFlatMultiFilter(items, "City")
                }
                placeholder="Select City"
              />
              <label className="mb-1" htmlFor="EnterZipCode">
                Zip Code
              </label>
              <AsyncMultiSelect
                searchType="zip"
                value={getValue(filters?.ZipCode)}
                onChange={(items) =>
                  handleSetSelectFlatMultiFilter(items, "ZipCode")
                }
                placeholder="Select Zip Code"
              />

              <label className="mb-1" htmlFor="Subdivisions">
                Subdivisions
              </label>
              <input
                type="text"
                className="form-control form-control--small mb-3"
                id="city_id"
                name="Subdivisions"
                placeholder="Subdivisions"
                onChange={handleSetFilter}
                value={filters.Subdivisions}
              />
              {filters?.neighborhood && (
                <span className="active_pts pill pill--location"><div className="folder--closeable">{labeledName(filters?.neighborhood)} &nbsp;<RemoveFilterButton label={labeledName(filters?.neighborhood)} onRemove={() => setFilters(prev => ({ ...prev, neighborhood: undefined }))} /></div></span>
              )}
              {Array.isArray(filters?.community) && filters?.community?.length > 0 && (
                <span className="active_pts pill pill--location"><div className="folder--closeable">{labeledName(filters?.community[0])} &nbsp;<RemoveFilterButton label={labeledName(filters?.community[0])} onRemove={() => setFilters(prev => ({ ...prev, community: [] }))} /></div></span>
              )}
              {filters?.nid && (
                <span className="active_pts pill pill--location"><div className="folder--closeable">{labeledName(filters?.nid)} &nbsp;<RemoveFilterButton label={labeledName(filters?.nid)} onRemove={() => setFilters(prev => ({ ...prev, nid: undefined }))} /></div></span>
              )}
              <label className="mb-1" htmlFor="Address">
                Address
              </label>
              <Typeahead
                id="Address"
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, Address: "" }))
                }
                identifier="addresses"
                selectedValue={filters.Address || ""}
                items={searchedList.addresses || []}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: IItem) =>
                  handleSetTypeAheadFilter("Address", item.myval || "")
                }
                renderItem={(item) => <>{item.myval}</>}
                resetSchoolList={() => resetSchoolList("addresses")}
              />

              <label className="mb-1" htmlFor="County">
                County
              </label>
              <select
                data-bitmask={0}
                className="selectpicker  highlight  KeyMap custom-select nfilter custom-select_medium mb-3"
                id="County"
                autoComplete="off"
                name="County"
                onChange={handleSetSelectFilter}
                value={filters.County?.value ?? ""}
              >
                {countiesLookup.map(({ label, value }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {/* Drive Time — har.com's "Drive Time" location mode (same three
                  controls as the site's search-filter panel). The destination is
                  the anchor: the times are only sent along with it. */}
              <label className="mb-1" htmlFor="drivetime_address">
                Drive Time: Arrival Location
              </label>
              {/* Picking a suggestion sets the address and its center together.
                  A search seeded with coordinates only (a HAR.com URL without an
                  address) shows them as the chip, so the user can see the
                  drive-time restriction is on and clear it. */}
              <Typeahead
                id="drivetime_address"
                identifier="drivetime"
                placeholder="Enter Arrival Location"
                minLength={3}
                selectedValue={filters.drivetime_address || filters.drivetime_center || ""}
                items={searchedList.drivetime}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: DriveTimeSuggestion) => {
                  setFilters((prev) => ({ ...prev, drivetime_address: item.address, drivetime_center: item.center }));
                  resetSchoolList("drivetime");
                }}
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, drivetime_address: undefined, drivetime_center: undefined }))
                }
                renderItem={(item) => <>{item.address}</>}
                resetSchoolList={() => resetSchoolList("drivetime")}
              />
              <div className="row small-gutters">
                <div className="col-6">
                  <label className="mb-1" htmlFor="drivetime_arrival_time">
                    Arrival Time
                  </label>
                  <select
                    className="custom-select custom-select_medium nfilter mb-3"
                    id="drivetime_arrival_time"
                    name="drivetime_arrival_time"
                    autoComplete="off"
                    onChange={handleSetSelectFilter}
                    value={filters.drivetime_arrival_time ?? ""}
                  >
                    {driveTimeArrivalTimeOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6">
                  <label className="mb-1" htmlFor="drivetime_travel_time">
                    Travel Time
                  </label>
                  <select
                    className="custom-select custom-select_medium nfilter mb-3"
                    id="drivetime_travel_time"
                    name="drivetime_travel_time"
                    autoComplete="off"
                    onChange={handleSetSelectFilter}
                    value={filters.drivetime_travel_time ?? ""}
                  >
                    {driveTimeTravelTimeOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="mb-1" htmlFor="MLSNumber">
                MLS Number
              </label>
              <input
                type="text"
                className="form-control form-control--small mb-1"
                id="city_id"
                name="MLSNumber"
                placeholder="MLS Number"
                onChange={handleSetFilter}
                value={filters.MLSNumber}
              />
              {/* {!isOpenMoreFilter && (<a className="btn--ordinary_blue" href="#" onClick={(e) => { e.preventDefault(); setIsOpenMoreFilter(true); }}>More Location Filter</a>)} */}
              <label className="mb-1 mt-3" htmlFor="marketarea">
                MLS Market Area
              </label>
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3"
                id="marketarea"
                name="marketarea"
                onChange={handleSetSelectFilter}
                value={filters.marketarea}
              >
                {areas.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {isOpenMoreFilter && (
                <>
                  <label className="mb-1" htmlFor="KeyMap">
                    Key Map
                  </label>
                  <input
                    type="text"
                    className="form-control form-control--small mb-3"
                    id="KeyMap"
                    name="KeyMap"
                    placeholder="Key Map"
                    onChange={handleSetFilter}
                    value={filters.KeyMap}
                  />

                  <label className="mb-1" htmlFor="MLSArea">
                    MLS Area
                  </label>
                  <select
                    data-bitmask={0}
                    className="custom-select custom-select_medium nfilter mb-3"
                    id="MLSArea"
                    name="MLSArea"
                    onChange={handleSetSelectFilter}
                    value={filters.MLSArea}
                  >
                    {mlsAreas.map(({ label }, index) => (
                      <option key={index} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>

                  <a
                    className="btn--ordinary_blue"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpenMoreFilter(false);
                    }}
                  >
                    Less Location Filters
                  </a>
                </>
              )}
            </div>

          </Tab>
          <Tab
            eventKey="School"
            title="School"
            className="mt-5 animate animate__slideInUp"
          >
            <div className="px-4 pb-2">
              <label className="mb-1" htmlFor="District">
                School District Name{" "}
              </label>
              <Typeahead
                id="District"
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, school_district: undefined }))
                }
                identifier="D"
                selectedValue={filters?.school_district?.label ?? ''}
                items={searchedList.D}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: IItem) => {
                  handleSetTypeAheadFilter(
                    "school_district",
                    { value: item.base_id, label: item.myval }
                  );
                }}
                renderItem={(item) => {
                  const city = item.mycity?.trim();
                  const label = city ? `${item.myval} (in ${city})` : item.myval;
                  return <div title={label} className="truncate-1-line">{label}</div>;
                }}
                resetSchoolList={() => resetSchoolList("D")}
              />

              <label className="mb-1" htmlFor="Elementary">
                Elementary School Name{" "}
              </label>
              <Typeahead
                id="Elementary"
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, elemantary_school: undefined }))
                }
                identifier="E"
                selectedValue={filters?.elemantary_school?.label ?? ''}
                items={searchedList.E}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: IItem) =>
                  handleSetTypeAheadFilter(
                    "elemantary_school",
                    { value: item.base_id, label: item.myval }
                  )
                }
                renderItem={(item) => {
                  const city = item.mycity?.trim();
                  const label = city ? `${item.myval} (in ${city})` : item.myval;
                  return <div title={label} className="truncate-1-line">{label}</div>;
                }}
                resetSchoolList={() => resetSchoolList("E")}
              />

              <label className="mb-1" htmlFor="Middle">
                Middle School Name
              </label>
              <Typeahead
                id="Middle"
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, middle_school: undefined }))
                }
                identifier="M"
                selectedValue={filters?.middle_school?.label ?? ''}
                items={searchedList.M}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: IItem) =>
                  handleSetTypeAheadFilter(
                    "middle_school",
                    { value: item.base_id, label: item.myval }
                  )
                }
                renderItem={(item) => {
                  const city = item.mycity?.trim();
                  const label = city ? `${item.myval} (in ${city})` : item.myval;
                  return <div title={label} className="truncate-1-line">{label}</div>;
                }}
                resetSchoolList={() => resetSchoolList("M")}
              />

              <label className="mb-1" htmlFor="High">
                High School Name{" "}
              </label>
              <Typeahead
                id="High"
                unSelected={() =>
                  setFilters((prev) => ({ ...prev, high_school: undefined }))
                }
                identifier="H"
                selectedValue={filters?.high_school?.label ?? ''}
                items={searchedList.H}
                onSearch={(text, identifier) => handleSearch(identifier, text)}
                onSelect={(item: IItem) =>
                  handleSetTypeAheadFilter(
                    "high_school",
                    { value: item.base_id, label: item.myval }
                  )
                }
                renderItem={(item) => {
                  const city = item.mycity?.trim();
                  const label = city ? `${item.myval} (in ${city})` : item.myval;
                  return <div title={label} className="truncate-1-line">{label}</div>;
                }}
                resetSchoolList={() => resetSchoolList("H")}
              />
            </div>
          </Tab>
        </Tabs>
        <div className="px-4 pb-4">
          <label className="mb-1" htmlFor="Criteria" style={{ fontSize: '27px', color: '#000', letterSpacing: '-0.64px' }}>
            Criteria
          </label>
          <div className="row mb-md-0 mb-3">
            <div className="col-auto pb-md-3" id="forms_for_sale">
              <div
                role="radiogroup"
                className="type__toggle d-flex"
                style={{ maxWidth: 300 }}
              >
                <label
                  role="radio"
                  aria-checked={filters.For === "Sale" || filters.For !== "Rent"}
                  className="custom-control custom-radio custom-radio__forsale custom-control-inline pl-5 cursor-pointer"
                  id="fdiv1"
                  htmlFor="for_sale_id"
                >
                  <input
                    type="radio"
                    id="for_sale_id"
                    name="For"
                    onChange={handleSetFilter}
                    value="Sale"
                    data-bitmask={0}
                    className="custom-control-input"
                    checked={filters.For === "Sale" || filters.For !== "Rent"}
                  />
                  <span
                    className="custom-control-label"
                    id="for_salebutton"
                  >
                    For Sale
                  </span>
                </label>
                <label
                  role="radio"
                  aria-checked={filters.For === "Rent"}
                  className="custom-control custom-radio custom-radio__forrent custom-control-inline pl-5 cursor-pointer"
                  id="fdiv2"
                  htmlFor="for_rent_id"
                >
                  <input
                    type="radio"
                    id="for_rent_id"
                    name="For"
                    value="Rent"
                    data-bitmask={0}
                    className="custom-control-input"
                    checked={filters.For === "Rent"}
                    onChange={handleSetFilter}
                  />
                  <span
                    className="custom-control-label"
                    id="for_rentbutton"
                  >
                    For Rent
                  </span>
                </label>
              </div>
            </div>
          </div>
          <label className="mb-1" htmlFor="Status">
            Status Availability{" "}
          </label>
          <select
            data-bitmask={0}
            className="custom-select custom-select_medium nfilter mb-3"
            id="Status"
            name="Status"
            onChange={handleSetSelectFilter}
            value={filters.Status}
          >
            {filteredStatusOptions.map(({ value, label }, ind) => (
              <option key={ind} value={value}>
                {label}
              </option>
            ))}
          </select>


          <div className="mb-4" id="prop_type_selc">
            <label className="mb-1">
              Property Type
            </label>

            <div
              ref={propertyTypeScrollRef}
              className="d-flex"
              style={{ overflowX: 'auto', overflowY: 'hidden', gap: '10px', paddingBottom: '2px', scrollBehavior: 'auto' }}>
              {propertyTypeOptions.map(({ value, label, icon }, index) => (
                <PropertyType
                  key={index}
                  id={`propertyType${index + 1}`}
                  icon={icon}
                  label={label}
                  value={value}
                  checked={Array.isArray(filters.PropertyType) ? filters.PropertyType.includes(value) : false}
                />
              ))}
            </div>
          </div>
          <label className="mt-3 d-flex justify-content-between">
            <span>Property Status</span>
            <div className="d-flex">
              {propertyStatusSaleOptions.map(({ label, icon }, index) => (
                <img key={index} src={icon} alt={label} style={{ width: '20px', height: '20px', marginRight: '8px' }} />
              ))}
            </div>
          </label>
          <div className="row">
            <div className="col-12 " id="forms_for_sale">
              <MultiSelect
                options={
                  filters.For == "Rent"
                    ? propertyStatusRentOptions
                    : propertyStatusSaleOptions
                }
                value={categoryValue}
                onChange={(e: any) =>
                  handleSetSelectFlatMultiFilter(e, "Category")
                }
                labelledBy="Select"
                overrideStrings={{
                  selectSomeItems: "Select Property Status",
                  allItemsAreSelected: "All selected",
                  noOptions: "No matches found",
                  search: "Search property status…",
                }}
              />
            </div>
          </div>
          <label className="mt-3 pr-4" htmlFor="MLSMarketArea">
            Amenities
          </label>
          <div className="row">
            <div className="col-12 " id="forms_for_sale">
              <MultiSelect
                options={filterAmenitiesOptions}
                value={amenityValue}
                onChange={(e: any) => onAmenitiesChange(e)}
                labelledBy="Select"
                hasSelectAll={false}
                overrideStrings={{
                  selectSomeItems: "Select Amenities",
                  allItemsAreSelected: "All selected",
                  noOptions: "No matches found",
                  search: "Search amenities…",
                }}
              />
            </div>
          </div>
          <label className="mt-3 pr-4" htmlFor="MLSMarketArea">
            Price
          </label>
          <div className="row">
            <div className="col-6 " id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="MinimumPrice"
                onChange={handleSetSelectFilter}
                value={filters.MinimumPrice}
              >
                <option value={""}>No Min</option>
                {(filters.For === "Rent" ? rentMinPriceLookup : saleMinPriceLookup).map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <select
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="MaximumPrice"
                onChange={handleSetSelectFilter}
                value={filters.MaximumPrice}
              >
                {" "}
                <option value={""}>No Max</option>
                {priceMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Bedrooms
          </label>
          <div className="row">
            <div className="col-6 " id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="BedroomMin"
                onChange={handleSetSelectFilter}
                value={filters.BedroomMin}
              >
                <option value={""}>No Min</option>
                {bedroomLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 ">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="BedroomMax"
                onChange={handleSetSelectFilter}
                value={filters.BedroomMax}
              >
                <option value={""}>No Max</option>
                {bedroomMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Bathrooms
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="FullbathMin"
                onChange={handleSetSelectFilter}
                value={filters.FullbathMin}
              >
                <option value={""}>No Min</option>
                {bathroomLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="FullbathMax"
                onChange={handleSetSelectFilter}
                value={filters.FullbathMax}
              >
                <option value={""}>No Max</option>
                {fullbathMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="mb-1 " htmlFor="MLSMarketArea">
            Half Bathrooms
          </label>
          <div className="row">
            <div className="col-12 ">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="HalfbathAvailability"
                onChange={handleSetSelectFilter}
                value={filters.HalfbathAvailability}
              >
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Building Sqft
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 mr-4 "
                id="marketarea_id"
                name="buildingSQFTMin"
                onChange={handleSetSelectFilter}
                value={filters.buildingSQFTMin}
              >
                {buildingLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="buildingSQFTMax"
                onChange={handleSetSelectFilter}
                value={filters.buildingSQFTMax}
              >
                <option value={""}>Any</option>
                {buildingSQFTMin.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Price/Sqft
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="PricesqftMin"
                onChange={handleSetSelectFilter}
                value={filters.PricesqftMin}
              >
                {priceSqt.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="PricesqftMax"
                onChange={handleSetSelectFilter}
                value={filters.PricesqftMax}
              >
                <option value={""}>No Max</option>
                {priceSqMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Lot (Sqft)
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="LotsizeMin"
                onChange={handleSetSelectFilter}
                value={filters.LotsizeMin}
              >
                {lotSqt.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="LotsizeMax"
                onChange={handleSetSelectFilter}
                value={filters.LotsizeMax}
              >
                <option value={""}>Any</option>
                {lotSqMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Lot (Acres)
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="LotAcresMin"
                onChange={handleSetSelectFilter}
                value={filters.LotAcresMin}
              >
                <option value="">Any</option>
                {lotAcr
                  .filter(({ value }) => value !== "")
                  .map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="LotAcresMax"
                onChange={handleSetSelectFilter}
                value={filters.LotAcresMax}
              >
                <option value={""}>Any</option>
                {lotacrMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Garage
          </label>
          <div className="row">
            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="GarageNum"
                onChange={handleSetSelectFilter}
                value={filters.GarageNum}
              >
                {garageLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6" id="forms_for_sale">
              <select
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="GarageDesc"
                onChange={handleSetSelectFilter}
                value={filters.GarageDesc}
              >
                {garageLookupMax.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Year Built
          </label>
          <div className="row">
            <div className="col-6 " id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 mr-4"
                id="marketarea_id"
                name="YearbuildsMin"
                onChange={handleSetSelectFilter}
                value={filters.YearbuildsMin}
              >
                <option value={""}>Any</option>
                {yearBuild.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 " id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="YearbuildsMax"
                onChange={handleSetSelectFilter}
                value={filters.YearbuildsMax}
              >
                <option value={""}>Any</option>
                {yearBuildLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Stories
          </label>
          <MultiSelect
            options={storiesLookup}
            value={storyValue}
            onChange={(e: any) =>
              handleSetSelectFlatMultiFilter(e, "Stories")
            }
            labelledBy="Select"
            overrideStrings={{
              selectSomeItems: "Select Stories",
              allItemsAreSelected: "All selected",
              noOptions: "No matches found",
              search: "Search stories…",
            }}
          />
          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Style
          </label>
          <div className="row">
            <div className="col-12" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="Style"
                onChange={handleSetSelectFilter}
                value={filters.Style}
              >
                {styleLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            New / Recent Construction
          </label>

          <div className="row">
            <div className="col-12" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="NewConstr"
                onChange={handleSetSelectFilter}
                value={filters.NewConstr}
              >
                {constructionLookup.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Parking Space
          </label>
          <div className="row">
            <div className="col-12" id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="Parking"
                onChange={handleSetSelectFilter}
                value={filters.Parking}
              >
                {parkingSpacesOptions.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Finance Considered
          </label>
          <div className="row">
            <div className="col-12 " id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="Finance"
                onChange={handleSetSelectFilter}
                value={filters.Finance}
              >
                {financeOptions.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="mb-1 pr-4" htmlFor="MLSMarketArea">
            Maintenance Fee Max
          </label>
          <div className="row">
            <div className="col-12 " id="forms_for_sale">
              <select
                data-bitmask={0}
                className="custom-select custom-select_medium nfilter mb-3 "
                id="marketarea_id"
                name="HoafeeMax"
                onChange={handleSetSelectFilter}
                value={filters.HoafeeMax}
              >
                {maintenanceFeeOptions.map(({ value, label }, index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <HarExtraFilters
          extra={filters.extra}
          onChange={(extra) => setFilters((prev) => ({ ...prev, extra }))}
        />
        <div
          className="position-sticky bg_color_snow_white px-3 pt-3 pb-2 filters_footer_controls"
          style={{ bottom: 0, zIndex: 1000 }}
        >
          <div className="row small-gutters pb-2">
            <div className="col-6">
              <a
                onClick={() => {
                  trackEvent("AI Mode / Apply Listing Search Filters");
                  send?.(filters);
                  closePopup?.();
                }}
                className="btn btn--primary btn--large w-100 text-center cursor--pointer"
              >
                Apply
              </a>
            </div>
            <div className="col-6">
              <a
                onClick={handleResetFilters}
                className="btn btn--ordinary btn--large w-100 text-center cursor--pointer"
              >
                Reset
              </a>
            </div>
          </div>
          {/* <div className="text-right">
            <a
              href="https://www.har.com/search/dosearch?for_sale=1&view=map&showform=1"
              target="blank"
              className="font_size--medium"
            >
              Advanced filters
            </a>
          </div> */}
        </div>
      </div>
    </div>
  );
}